import {
  Grid,
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Button,
} from "@mui/material";
import {
  FaCoins,
  FaRobot,
  FaCommentDollar,
  FaChartLine,
  FaArrowUp,
  FaArrowDown,
  FaExternalLinkAlt,
} from "react-icons/fa";
import useApiEp from "../hooks/useApiEp";
import { roundToMaxDecimals } from "../utils/util";
import { useNavigate } from "react-router-dom";

const StatsCards = () => {
  const { data: BalanceData, isLoading } = useApiEp(
    `/user/get-account-balance`
  );
  const rootDomain = window.location.hostname;
  const navigate = useNavigate();

  // Calculate message counts based on rates
  const wabaBalance = BalanceData?.wallet ?? 0;
  const marketingRate = 0.79; // ₹0.79 per marketing message
  const utilityRate = 0.115; // ₹0.115 per utility message

  const marketingMessages = Math.floor(wabaBalance / marketingRate);
  const utilityMessages = Math.floor(wabaBalance / utilityRate);

  const stats = [
    {
      title: "Waba Credits",
      value: `₹${roundToMaxDecimals(wabaBalance, 2).toLocaleString()}`,
      change: "+12%",
      trend: "up",
      icon: <FaCoins />,
      color: "#9c27b0",
      creditLink: "/home/wallet-recharge",
      description: `<strong>WhatsApp Business API credits</strong><br/>
        <span style="color: #4CAF50; font-weight: 600;">~${marketingMessages.toLocaleString()} Marketing</span> messages  or 
        <span style="color: #2196F3; font-weight: 600;">~${utilityMessages.toLocaleString()} Utility</span> messages`,
      ...(rootDomain === "admin.smartgrowthai.com"
        ? { buttonText: "Recharge" }
        : {}),
    },
    {
      title: "AI Credits",
      value: `${BalanceData?.aiWallet ?? 0}`,
      change: "+3",
      trend: "up",
      icon: <FaRobot />,
      color: "#2e7d32",
      creditLink: "/home/ai/request-credits",
      description:
        "<strong>AI processing units</strong><br/>Power your AI conversations, analysis, and automation features",
      buttonText: "Recharge",
    },
    {
      title: "RCS Credits",
      value: `${Math.round(BalanceData?.rcsWalletRichMedia ?? 0)}`,
      change: "-2%",
      trend: "down",
      icon: <FaCommentDollar />,
      color: "#1976d2",
      creditLink: "/home/rcs/request-credits",
      description:
        "<strong>Rich Communication Services</strong><br/>Send rich media messages with images, buttons, and carousels",
      buttonText: "Purchase",
    },
    {
      title: "Current Plan",
      value: `${BalanceData?.plan ?? "NA"}`,
      change: "+0.5%",
      trend: "up",
      icon: <FaChartLine />,
      color: "#ed6c02",
      creditLink: "/pricing-plans",
      description:
        "<strong>Your active subscription</strong><br/>Includes features, limits, and service capabilities",
      buttonText: "Check Now",
    },
  ];

  return (
    <Grid container spacing={3} sx={{ mb: 3 }}>
      {stats.map((stat, index) => (
        <Grid item xs={12} sm={6} md={3} key={index}>
          <Card
            sx={{
              background: "rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              borderRadius: 4,
              boxShadow: "0 1px 3px 0 rgba(31, 38, 135, 0.37)",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "1px",
                background:
                  "linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)",
              },
              "&:hover": {
                transform: "translateY(-2px)",
                transition: "all 0.3s ease",
                boxShadow: "0 4px 12px 0 rgba(31, 38, 135, 0.4)",
              },
              transition: "all 0.3s ease",
            }}
          >
            {/* Liquid glass effect overlay */}
            <Box
              sx={{
                position: "absolute",
                top: "-50%",
                left: "-50%",
                width: "200%",
                height: "200%",
                background:
                  "linear-gradient(45deg, transparent, rgba(255,255,255,0.1), transparent)",
                transform: "rotate(45deg)",
                animation: "shimmer 3s infinite",
                "@keyframes shimmer": {
                  "0%": { transform: "translateX(-100%) rotate(45deg)" },
                  "100%": { transform: "translateX(100%) rotate(45deg)" },
                },
              }}
            />

            <CardContent sx={{ position: "relative", zIndex: 1, p: 3 }}>
              {/* Header with icon and prominent title */}
              <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${stat.color}40, ${stat.color}20)`,
                    border: `1px solid ${stat.color}30`,
                    color: stat.color,
                    mr: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "1.5rem",
                    flexShrink: 0,
                  }}
                >
                  {stat.icon}
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{
                      color: "text.primary",
                      fontSize: "1.1rem",
                      lineHeight: 1.2,
                      mb: 0.5,
                    }}
                  >
                    {stat.title}
                  </Typography>
                  {/* HTML formatted description */}
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.75rem",
                      lineHeight: 1.3,
                      opacity: 0.8,
                    }}
                    dangerouslySetInnerHTML={{ __html: stat.description }}
                  />
                </Box>
              </Box>

              {/* Value with trend chip on the right */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  sx={{
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    fontSize: "1.5rem",
                    lineHeight: 1.1,
                  }}
                >
                  {stat.value}
                </Typography>
                <Chip
                  label={stat.change}
                  size="small"
                  sx={{
                    background:
                      stat.trend === "up"
                        ? "linear-gradient(135deg, #4CAF50, #66BB6A)"
                        : "linear-gradient(135deg, #F44336, #EF5350)",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "0.75rem",
                    height: "26px",
                    minWidth: "60px",
                  }}
                  icon={
                    stat.trend === "up" ? (
                      <FaArrowUp size={12} />
                    ) : (
                      <FaArrowDown size={12} />
                    )
                  }
                />
              </Box>

              {/* Action button */}
              {stat.buttonText && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    navigate(stat.creditLink);
                  }}
                  endIcon={<FaExternalLinkAlt size={12} />}
                  sx={{
                    border: `1px solid ${stat.color}40`,
                    background: "rgba(255, 255, 255, 0.05)",
                    color: stat.color,
                    borderRadius: 3,
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    px: 2,
                    py: 0.5,
                    width: "100%",
                    "&:hover": {
                      background: `${stat.color}15`,
                      border: `1px solid ${stat.color}60`,
                      transform: "translateY(-1px)",
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  {stat.buttonText}
                </Button>
              )}
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default StatsCards;
